import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import {
    BehaviorSubject,
    Observable,
    Subscription,
    catchError,
    filter,
    finalize,
    map,
    of,
    shareReplay,
    tap,
    throwError
} from "rxjs";
import { environment } from "src/environments/environment";
import { normalizeFeatureSet } from "../utils/product_license_features.util";
import { normalizeDescopedSelections } from "../utils/product_license_descoped_selections.util";
import { AssessmentTaskScopeChangesService } from "./assessment_task_scope_changes.service";

interface ApiResponse<T> {
    success: boolean;
    data?: T;
    message?: string;
}

export interface ProductLicenseSummary {
    id: number;
    accountId: number | null;
    displayName: string | null;
    formType: string;
    aocType?: "Merchant" | "Service Provider" | null;
    formKey: string | null;
    featureSet: string | null;
    accountName: string | null;
    accountAddressLine1: string | null;
    accountAddressLine2: string | null;
    accountCity: string | null;
    accountRegion: string | null;
    accountPostalCode: string | null;
    accountCountryCode: string | null;
    accountWebsite: string | null;
    merchantId: number | null;
    merchantName: string | null;
    merchantPrimaryContactName: string | null;
    status: string | null;
    version: string | null;
    activationDate: string | null;
    expirationDate: string | null;
    assignedIsaUserId: number | null;
    assignedIsaUserIdFromAssignments: number | null;
    assignedIsaDisplayName: string | null;
    assignedQsaUserId: number | null;
    assignedQsaUserIdFromAssignments: number | null;
    assignedQsaDisplayName: string | null;
    assignedQsaEmail: string | null;
    assignedQsaPhoneNumber: string | null;
    assignedQsaCertificateNumber: string | null;
    assignedQaUserId: number | null;
    assignedQaUserIdFromAssignments: number | null;
    hasAssignedQa?: boolean;
    assignedQaAccountName: string | null;
    assignedQaDisplayName: string | null;
    userReaderIds: number[];
    editorIds: number[];
    merchantRepUserId: number | null;
    merchantRepDisplayName: string | null;
    descopedSelections?: string[];
    responseSummary: ProductLicenseResponseSummary;
}

export interface TaskAssessmentChoice {
    canCreateTask?: boolean;
    id: number;
    merchantId: number;
    displayName: string | null;
    merchantName: string | null;
    formType: string;
    formKey: "roc_form" | "saq_form";
}

export interface ProductLicenseResponseSummary {
    draftCount: number;
    approvedCount: number;
    qsaReviewedCount: number;
    lastUpdatedAt: string | null;
    lastUpdatedBy: string | null;
    lastUpdatedSource?: "responses" | "activation" | null;
    sections: Record<string, {
        draftCount: number;
        approvedCount: number;
        qsaReviewedCount: number;
    }>;
}



export interface AccountLicenseCredits {
    licenseCredits: number;
}
export interface ProductLicenseCreatePayload {
    merchantId: number;
    formType: string;
    aocType?: "Merchant" | "Service Provider" | null;
    featureSet: string;
    displayName?: string | null;
    rocDefaultRequirementSelection?: "not_tested" | "not_applicable" | null;
    rocAutocompleteRequirements?: boolean;
    rocAutocompleteSaqTypes?: string[];
    rocAutocompleteText?: string | null;
    descopedSelections?: string[] | null;
    adminConsoleCustomerCreation?: boolean;
    expirationDuration?: "one_month" | "one_year";
}

type NullableLicenseList = ProductLicenseSummary[] | null;

@Injectable({ providedIn: "root" })
export class ProductLicensesService {
    private readonly apiBaseUrl = environment.baseurl.replace(/\/$/, "");

    private readonly licensesSubject = new BehaviorSubject<NullableLicenseList>(null);
    private readonly accountLicenseCreditsSubject = new BehaviorSubject<AccountLicenseCredits | null>(null);
    private pendingRequest$: Observable<ProductLicenseSummary[]> | null = null;
    private pendingAccountLicenseCreditsRequest$: Observable<AccountLicenseCredits> | null = null;
    private currentLoadSubscription: Subscription | null = null;
    private taskAccessSnapshot: Map<number, string> | null = null;
    private cacheGeneration = 0;
    private readonly pendingAccessOverrides = new Map<number, ProductLicenseSummary | null>();

    constructor(
        private readonly http: HttpClient,
        private readonly taskScopeChanges: AssessmentTaskScopeChangesService
    ) {}

    loadLicenses(forceRefresh = false): Observable<ProductLicenseSummary[]> {
        const cached = this.licensesSubject.value;
        if (!forceRefresh && Array.isArray(cached)) {
            return of(cached);
        }

        if (!this.pendingRequest$) {
            const generation = this.cacheGeneration;
            this.pendingAccessOverrides.clear();
            this.pendingRequest$ = this.fetchLicensesFromApi().pipe(
                map((response) => this.validateResponse(response)),
                map((licenses) => licenses.map((license) => this.normalizeLicense(license))),
                map((licenses) => licenses.filter((license): license is ProductLicenseSummary => !!license)),
                map((licenses) => {
                    if (generation !== this.cacheGeneration) return licenses;
                    const merged = licenses.filter((license) => !this.pendingAccessOverrides.has(license.id));
                    for (const license of this.pendingAccessOverrides.values()) if (license) merged.push(license);
                    this.pendingAccessOverrides.clear();
                    return merged;
                }),
                finalize(() => {
                    if (generation === this.cacheGeneration) this.pendingRequest$ = null;
                }),
                shareReplay(1)
            );

            this.currentLoadSubscription?.unsubscribe();
            this.currentLoadSubscription = this.pendingRequest$.subscribe({
                next: (licenses) => {
                    if (generation !== this.cacheGeneration) return;
                    this.licensesSubject.next(licenses);
                    this.publishTaskAccessChanges(licenses);
                },
                error: () => {
                    if (generation !== this.cacheGeneration) return;
                    this.licensesSubject.next([]);
                    this.currentLoadSubscription = null;
                },
                complete: () => {
                    if (generation !== this.cacheGeneration) return;
                    this.currentLoadSubscription = null;
                }
            });
        }

        return this.pendingRequest$;
    }

    loadTaskAssessmentChoices(): Observable<TaskAssessmentChoice[]> {
        return this.http
            .get<ApiResponse<TaskAssessmentChoice[]>>(`${this.apiBaseUrl}/product-licenses/task-assessment-choices`)
            .pipe(map((response) => {
                if (!response?.success || !Array.isArray(response.data)) {
                    throw new Error(response?.message || "Unable to load task assessments.");
                }
                return response.data.map((choice) => {
                    if (!Number.isSafeInteger(choice?.id) || choice.id <= 0 ||
                        !Number.isSafeInteger(choice?.merchantId) || choice.merchantId <= 0 ||
                        (choice.formKey !== "roc_form" && choice.formKey !== "saq_form") ||
                        typeof choice.formType !== "string" ||
                        (choice.displayName !== null && typeof choice.displayName !== "string") ||
                        (choice.merchantName !== null && typeof choice.merchantName !== "string")) {
                        throw new Error("Unexpected task assessment choice response.");
                    }
                    return choice;
                });
            }));
    }

    /** Refresh only committed assignment scope; the caller fences actor/session and newer reads. */
    refreshAssessmentAccess(licenseId: number, isCurrent: () => boolean): Observable<ProductLicenseSummary | null> {
        if (!Number.isSafeInteger(licenseId) || licenseId <= 0) {
            return throwError(() => new Error("Invalid assessment."));
        }
        // Overlay exact results on an older in-flight inventory read without
        // discarding the rest of that inventory or starting another full read.
        const apply = (license: ProductLicenseSummary | null) => {
            if (!isCurrent()) return;
            const cached = this.licensesSubject.value;
            if (this.pendingRequest$) this.pendingAccessOverrides.set(licenseId, license);
            if (!Array.isArray(cached)) {
                return;
            }
            const next = cached.filter((item) => item.id !== licenseId);
            if (license) {
                const index = cached.findIndex((item) => item.id === licenseId);
                next.splice(index < 0 ? next.length : index, 0, license);
            }
            this.licensesSubject.next(next);
            this.publishTaskAccessChanges(next);
        };
        return this.http.get<ApiResponse<ProductLicenseSummary>>(`${this.apiBaseUrl}/product-licenses/${licenseId}`).pipe(
            map((response) => {
                const license = response?.success ? this.normalizeLicense(response.data ?? null) : null;
                if (!license || license.id !== licenseId) throw new Error("Assessment unavailable.");
                return license;
            }),
            tap(apply),
            catchError((error: unknown) => {
                // An unreadable permission snapshot must not leave editable
                // cached assessment content available in the active shell.
                apply(null);
                return throwError(() => error);
            })
        );
    }

    watchLicenses(): Observable<ProductLicenseSummary[]> {
        if (!this.licensesSubject.value && !this.pendingRequest$) {
            void this.loadLicenses().subscribe({ error: () => {} });
        }

        return this.licensesSubject.asObservable().pipe(
            filter((licenses): licenses is ProductLicenseSummary[] => Array.isArray(licenses))
        );
    }

    hasAccountProductLicenses(): Observable<boolean | null> {
        return this.http
            .get<ApiResponse<{ hasProductLicenses: boolean }>>(
                `${this.apiBaseUrl}/product-licenses/account/has-licenses`
            )
            .pipe(
                map((response) => {
                    if (!response?.success) {
                        throw new Error(response?.message || "Unable to check account assessments.");
                    }

                    if (typeof response.data?.hasProductLicenses !== "boolean") {
                        throw new Error("Unexpected response while checking account assessments.");
                    }

                    return response.data.hasProductLicenses;
                }),
                catchError(() => of(null))
            );
    }

    getLicenseById(licenseId: number): Observable<ProductLicenseSummary | null> {
        return this.watchLicenses().pipe(
            map((licenses) => licenses.find((license) => license.id === licenseId) || null)
        );
    }

    getFirstLicenseByFormKey(formKey: string): Observable<ProductLicenseSummary | null> {
        return this.watchLicenses().pipe(
            map((licenses) => licenses.find((license) => license.formKey === formKey) || null)
        );
    }


    getAccountLicenseCredits(forceRefresh = false): Observable<AccountLicenseCredits> {
        const cachedCredits = this.accountLicenseCreditsSubject.value;
        if (!forceRefresh && cachedCredits) {
            return of(cachedCredits);
        }

        if (this.pendingAccountLicenseCreditsRequest$) {
            return this.pendingAccountLicenseCreditsRequest$;
        }

        this.pendingAccountLicenseCreditsRequest$ = this.http
            .get<ApiResponse<{ licenseCredits: number }>>(`${this.apiBaseUrl}/product-licenses/account/license-credits`)
            .pipe(
                map((response) => {
                    if (!response?.success) {
                        throw new Error(response?.message || 'Unable to load assessment credits.');
                    }

                    return { licenseCredits: Number(response?.data?.licenseCredits) || 0 };
                }),
                map((credits) => {
                    this.accountLicenseCreditsSubject.next(credits);
                    return credits;
                }),
                catchError((error) =>
                    throwError(() => new Error(error?.error?.message || error?.message || 'Unable to load assessment credits.'))
                ),
                shareReplay(1),
                finalize(() => {
                    this.pendingAccountLicenseCreditsRequest$ = null;
                })
            );

        return this.pendingAccountLicenseCreditsRequest$;
    }


    watchAccountLicenseCredits(): Observable<AccountLicenseCredits> {
        if (!this.accountLicenseCreditsSubject.value && !this.pendingAccountLicenseCreditsRequest$) {
            void this.getAccountLicenseCredits().subscribe({ error: () => {} });
        }

        return this.accountLicenseCreditsSubject.asObservable().pipe(
            filter((credits): credits is AccountLicenseCredits => !!credits)
        );
    }

    setAccountLicenseCredits(licenseCredits: number): void {
        this.accountLicenseCreditsSubject.next({ licenseCredits: Math.max(0, Number(licenseCredits) || 0) });
    }

    upgradeProductLicenseFeatureSet(licenseId: number): Observable<ProductLicenseSummary> {
        return this.http
            .put<ApiResponse<ProductLicenseSummary>>(`${this.apiBaseUrl}/product-licenses/${licenseId}/upgrade-feature-set`, {})
            .pipe(
                map((response) => this.validateSingleResponse(response)),
                map((license) => {
                    const normalized = this.normalizeLicense(license);
                    if (!normalized) {
                        throw new Error('Unable to upgrade assessment.');
                    }
                    return normalized;
                }),
                catchError((error) =>
                    throwError(() => new Error(error?.error?.message || error?.message || 'Unable to upgrade assessment.'))
                )
            );
    }

    createProductLicense(payload: ProductLicenseCreatePayload): Observable<ProductLicenseSummary> {
        return this.http
            .post<ApiResponse<ProductLicenseSummary>>(`${this.apiBaseUrl}/product-licenses`, payload)
            .pipe(
                map((response) => this.validateSingleResponse(response)),
                map((license) => {
                    const normalized = this.normalizeLicense(license);
                    if (!normalized) {
                        throw new Error("Unable to create assessment.");
                    }
                    return normalized;
                }),
                catchError((error) =>
                    throwError(() => new Error(error?.error?.message || error?.message || "Unable to create assessment."))
                )
            );
    }

    deleteProductLicense(licenseId: number): Observable<void> {
        return this.http
            .delete<ApiResponse<null>>(`${this.apiBaseUrl}/product-licenses/${licenseId}`)
            .pipe(
                map((response) => {
                    if (!response?.success) {
                        throw new Error(response?.message || "Unable to delete assessment.");
                    }

                    return void 0;
                }),
                catchError((error) =>
                    throwError(() => new Error(error?.error?.message || error?.message || "Unable to delete assessment."))
                )
            );
    }

    private fetchLicensesFromApi(): Observable<ApiResponse<ProductLicenseSummary[]>> {
        return this.http
            .get<ApiResponse<ProductLicenseSummary[]>>(`${this.apiBaseUrl}/product-licenses`)
            .pipe(
                catchError((error) =>
                    throwError(() =>
                        new Error(
                            error?.error?.message || error?.message || "Unable to load product licenses."
                        )
                    )
                )
            );
    }

    private validateResponse(response: ApiResponse<ProductLicenseSummary[]>): ProductLicenseSummary[] {
        if (!response?.success || !Array.isArray(response.data)) {
            throw new Error(response?.message || "Product licenses could not be loaded.");
        }

        return response.data;
    }

    private validateSingleResponse(response: ApiResponse<ProductLicenseSummary>): ProductLicenseSummary {
        if (!response?.success || !response.data) {
            throw new Error(response?.message || "Unable to create assessment.");
        }

        return response.data;
    }

    private normalizeLicense(raw: ProductLicenseSummary | null | undefined): ProductLicenseSummary | null {
        if (!raw || typeof raw !== "object") {
            return null;
        }

        const id = Number((raw as { id?: unknown }).id);
        if (!Number.isInteger(id) || id <= 0) {
            return null;
        }

        const accountIdValue =
            (raw as { accountId?: unknown; account_id?: unknown }).accountId ??
            (raw as { account_id?: unknown }).account_id;
        const accountIdNumeric = Number(accountIdValue);
        const accountId = Number.isInteger(accountIdNumeric) ? accountIdNumeric : null;
        const displayName = this.normalizeOptionalString(
            (raw as { displayName?: unknown; display_name?: unknown }).displayName ??
                (raw as { display_name?: unknown }).display_name
        );
        const formTypeValue = typeof raw.formType === "string" ? raw.formType : "";
        const formType = formTypeValue.trim();
        const rawAocType =
            (raw as { aocType?: unknown; aoc_type?: unknown }).aocType ??
            (raw as { aoc_type?: unknown }).aoc_type;
        const aocType =
            typeof rawAocType === "string" && rawAocType.trim().toLowerCase() === "service provider"
                ? "Service Provider"
                : typeof rawAocType === "string" && rawAocType.trim().toLowerCase() === "merchant"
                    ? "Merchant"
                    : null;
        const rawFormKey = typeof raw.formKey === "string" && raw.formKey.trim() ? raw.formKey.trim() : null;
        const formKey = rawFormKey ?? this.deriveFormKey(formType);
        const featureSet =
            normalizeFeatureSet(
                (raw as { featureSet?: unknown; feature_set?: unknown }).featureSet ??
                    (raw as { feature_set?: unknown }).feature_set
            ) ?? "free_trial";
        const accountName = this.normalizeOptionalString(
            (raw as { accountName?: unknown; account_name?: unknown }).accountName ??
                (raw as { account_name?: unknown }).account_name
        );
        const accountAddressLine1 = this.normalizeOptionalString(
            (raw as { accountAddressLine1?: unknown; address_line1?: unknown }).accountAddressLine1 ??
                (raw as { address_line1?: unknown }).address_line1
        );
        const accountAddressLine2 = this.normalizeOptionalString(
            (raw as { accountAddressLine2?: unknown; address_line2?: unknown }).accountAddressLine2 ??
                (raw as { address_line2?: unknown }).address_line2
        );
        const accountCity = this.normalizeOptionalString(
            (raw as { accountCity?: unknown }).accountCity ?? (raw as { city?: unknown }).city
        );
        const accountRegion = this.normalizeOptionalString(
            (raw as { accountRegion?: unknown }).accountRegion ?? (raw as { region?: unknown }).region
        );
        const accountPostalCode = this.normalizeOptionalString(
            (raw as { accountPostalCode?: unknown; postal_code?: unknown }).accountPostalCode ??
                (raw as { postal_code?: unknown }).postal_code
        );
        const accountCountryCode = this.normalizeOptionalString(
            (raw as { accountCountryCode?: unknown; country_code?: unknown }).accountCountryCode ??
                (raw as { country_code?: unknown }).country_code
        );
        const accountWebsite = this.normalizeOptionalString(
            (raw as { accountWebsite?: unknown; website?: unknown }).accountWebsite ??
                (raw as { website?: unknown }).website
        );
        const merchantIdValue = (raw as { merchantId?: unknown }).merchantId;
        const merchantId = Number.isInteger(merchantIdValue) ? Number(merchantIdValue) : null;
        const merchantName = typeof raw.merchantName === "string" ? raw.merchantName.trim() || null : null;
        const merchantPrimaryContactName = this.normalizeDisplayName(
            (raw as { merchantPrimaryContactName?: unknown; merchant_primary_contact_name?: unknown })
                .merchantPrimaryContactName ?? (raw as { merchant_primary_contact_name?: unknown }).merchant_primary_contact_name
        );
        const status = typeof raw.status === "string" ? raw.status.trim() || null : null;
        const version = typeof raw.version === "string" ? raw.version.trim() || null : null;
        const activationDate = typeof raw.activationDate === "string" ? raw.activationDate : null;
        const expirationDate = typeof raw.expirationDate === "string" ? raw.expirationDate : null;
        const assignedIsaUserId = this.normalizeUserId(
            (raw as { assignedIsaUserId?: unknown }).assignedIsaUserId
        );
        const assignedIsaUserIdFromAssignments = this.normalizeUserId(
            (raw as { assignedIsaUserIdFromAssignments?: unknown }).assignedIsaUserIdFromAssignments
        );
        const assignedIsaDisplayName = this.normalizeDisplayName(
            (raw as { assignedIsaDisplayName?: unknown }).assignedIsaDisplayName
        );
        const assignedQsaUserId = this.normalizeUserId(
            (raw as { assignedQsaUserId?: unknown }).assignedQsaUserId
        );
        const assignedQsaUserIdFromAssignments = this.normalizeUserId(
            (raw as { assignedQsaUserIdFromAssignments?: unknown }).assignedQsaUserIdFromAssignments
        );
        const assignedQsaDisplayName = this.normalizeDisplayName(
            (raw as { assignedQsaDisplayName?: unknown }).assignedQsaDisplayName
        );
        const assignedQsaEmail = this.normalizeOptionalString(
            (raw as { assignedQsaEmail?: unknown; assigned_qsa_email?: unknown }).assignedQsaEmail ??
                (raw as { assigned_qsa_email?: unknown }).assigned_qsa_email
        );
        const assignedQsaPhoneNumber = this.normalizeOptionalString(
            (raw as { assignedQsaPhoneNumber?: unknown; assigned_qsa_phone_number?: unknown }).assignedQsaPhoneNumber ??
                (raw as { assigned_qsa_phone_number?: unknown }).assigned_qsa_phone_number
        );
        const assignedQsaCertificateNumber = this.normalizeOptionalString(
            (raw as { assignedQsaCertificateNumber?: unknown; assigned_qsa_certificate_number?: unknown })
                .assignedQsaCertificateNumber ??
                (raw as { assigned_qsa_certificate_number?: unknown }).assigned_qsa_certificate_number
        );
        const assignedQaUserId = this.normalizeUserId(
            (raw as { assignedQaUserId?: unknown }).assignedQaUserId
        );
        const assignedQaUserIdFromAssignments = this.normalizeUserId(
            (raw as { assignedQaUserIdFromAssignments?: unknown }).assignedQaUserIdFromAssignments
        );
        const hasAssignedQa = (raw as { hasAssignedQa?: unknown }).hasAssignedQa === true;
        const assignedQaAccountName = this.normalizeDisplayName(
            (raw as { assignedQaAccountName?: unknown }).assignedQaAccountName
        );
        const assignedQaDisplayName = this.normalizeDisplayName(
            (raw as { assignedQaDisplayName?: unknown }).assignedQaDisplayName
        );
        const userReaderIds = this.normalizeUserReaderIds(
            (raw as { userReaderIds?: unknown }).userReaderIds ??
                (raw as { user_readers?: unknown }).user_readers
        );
        const editorIds = this.normalizeUserIdArray(
            (raw as { editorIds?: unknown; editors?: unknown }).editorIds ??
                (raw as { editors?: unknown }).editors ??
                (raw as { user_assignments?: { editors?: unknown } }).user_assignments?.editors ??
                (raw as { userAssignments?: { editors?: unknown } }).userAssignments?.editors
        );
        const merchantRepUserId = this.normalizeUserId(
            (raw as { merchantRepUserId?: unknown; merchantRepId?: unknown; merchant_rep?: unknown })
                .merchantRepUserId ??
                (raw as { merchantRepId?: unknown }).merchantRepId ??
                (raw as { merchant_rep?: unknown }).merchant_rep ??
                (raw as { user_assignments?: { merchant_rep?: unknown; merchantRep?: unknown } })
                    .user_assignments?.merchant_rep ??
                (raw as { userAssignments?: { merchantRep?: unknown; merchant_rep?: unknown } })
                    .userAssignments?.merchantRep ??
                (raw as { userAssignments?: { merchant_rep?: unknown } }).userAssignments?.merchant_rep
        );
        const merchantRepDisplayName = this.normalizeDisplayName(
            (raw as { merchantRepDisplayName?: unknown; merchant_rep_display_name?: unknown }).merchantRepDisplayName ??
                (raw as { merchant_rep_display_name?: unknown }).merchant_rep_display_name
        );
        const descopedSelections = normalizeDescopedSelections(
            (raw as { descopedSelections?: unknown; descoped_selections?: unknown }).descopedSelections ??
                (raw as { descoped_selections?: unknown }).descoped_selections
        );
        const responseSummary = this.normalizeResponseSummary(
            (raw as { responseSummary?: unknown }).responseSummary ??
                (raw as { response_summary?: unknown }).response_summary ??
                null
        );

        return {
            id,
            accountId,
            displayName,
            formType,
            aocType,
            formKey,
            featureSet,
            accountName,
            accountAddressLine1,
            accountAddressLine2,
            accountCity,
            accountRegion,
            accountPostalCode,
            accountCountryCode,
            accountWebsite,
            merchantId,
            merchantName,
            merchantPrimaryContactName,
            status,
            version,
            activationDate,
            expirationDate,
            assignedIsaUserId,
            assignedIsaUserIdFromAssignments,
            assignedIsaDisplayName,
            assignedQsaUserId,
            assignedQsaUserIdFromAssignments,
            assignedQsaDisplayName,
            assignedQsaEmail,
            assignedQsaPhoneNumber,
            assignedQsaCertificateNumber,
            assignedQaUserId,
            assignedQaUserIdFromAssignments,
            hasAssignedQa,
            assignedQaAccountName,
            assignedQaDisplayName,
            userReaderIds,
            editorIds,
            merchantRepUserId,
            merchantRepDisplayName,
            descopedSelections,
            responseSummary
        };
    }

    private normalizeUserId(value: unknown): number | null {
        const numeric = Number(value);
        if (!Number.isInteger(numeric) || numeric <= 0) {
            return null;
        }

        return numeric;
    }

    private normalizeDisplayName(value: unknown): string | null {
        if (typeof value !== "string") {
            return null;
        }

        const trimmed = value.trim();
        return trimmed.length > 0 ? trimmed : null;
    }

    private normalizeOptionalString(value: unknown): string | null {
        if (value === undefined || value === null) {
            return null;
        }

        if (typeof value === "string") {
            const trimmed = value.trim();
            return trimmed.length > 0 ? trimmed : null;
        }

        const stringified = String(value).trim();
        return stringified.length > 0 ? stringified : null;
    }

    private normalizeUserReaderIds(value: unknown): number[] {
        return this.normalizeUserIdArray(value);
    }

    private normalizeUserIdArray(value: unknown): number[] {
        if (!value) {
            return [];
        }

        if (Array.isArray(value)) {
            return value
                .map((entry) => this.normalizeUserId(entry))
                .filter((id): id is number => Number.isInteger(id) && id > 0);
        }

        if (typeof value === "string") {
            try {
                const parsed = JSON.parse(value);
                if (Array.isArray(parsed)) {
                    return parsed
                        .map((entry) => this.normalizeUserId(entry))
                        .filter((id): id is number => Number.isInteger(id) && id > 0);
                }
            } catch (error) {
                return [];
            }
        }

        return [];
    }

    private deriveFormKey(formType: string): string | null {
        if (!formType) {
            return null;
        }

        const normalized = formType.toLowerCase();
        if (!normalized) {
            return null;
        }

        if (normalized.includes("saq")) {
            return "saq_form";
        }

        if (normalized.includes("roc")) {
            return "roc_form";
        }

        if (normalized.includes("nist")) {
            return "nist_csf_form";
        }

        if (normalized.includes("iso")) {
            return "iso_form";
        }

        if (normalized.includes("soc")) {
            return "soc2_form";
        }

        return null;
    }

    private normalizeResponseSummary(raw: unknown): ProductLicenseResponseSummary {
        const toCount = (value: unknown): number => {
            const numeric = Number(value);
            if (!Number.isFinite(numeric)) {
                return 0;
            }

            const truncated = Math.floor(numeric);
            return truncated > 0 ? truncated : 0;
        };

        const normalizeSections = (
            sections: unknown
        ): Record<string, { draftCount: number; approvedCount: number; qsaReviewedCount: number }> => {
            if (!sections || typeof sections !== "object") {
                return {};
            }

            const result: Record<string, { draftCount: number; approvedCount: number; qsaReviewedCount: number }> = {};

            const assignSection = (key: unknown, value: unknown): void => {
                if (typeof key !== "string") {
                    return;
                }

                const sectionKey = key.trim();
                if (!sectionKey) {
                    return;
                }

                const draftCount = toCount((value as { draftCount?: unknown; draft_count?: unknown })?.draftCount ?? (value as { draft_count?: unknown })?.draft_count);
                const approvedCount = toCount(
                    (value as { approvedCount?: unknown; approved_count?: unknown })?.approvedCount ??
                        (value as { approved_count?: unknown })?.approved_count
                );
                const qsaReviewedCount = toCount(
                    (value as { qsaReviewedCount?: unknown; qsa_reviewed_count?: unknown })?.qsaReviewedCount ??
                        (value as { qsa_reviewed_count?: unknown })?.qsa_reviewed_count
                );

                result[sectionKey] = { draftCount, approvedCount, qsaReviewedCount };
            };

            if (sections instanceof Map) {
                sections.forEach((value, key) => assignSection(key, value));
                return result;
            }

            if (Array.isArray(sections)) {
                sections.forEach((entry) => {
                    if (Array.isArray(entry) && entry.length >= 2) {
                        assignSection(entry[0], entry[1]);
                    }
                });
                return result;
            }

            Object.entries(sections as Record<string, unknown>).forEach(([key, value]) => assignSection(key, value));

            return result;
        };

        if (!raw || typeof raw !== "object") {
            return {
                draftCount: 0,
                approvedCount: 0,
                qsaReviewedCount: 0,
                lastUpdatedAt: null,
                lastUpdatedBy: null,
                lastUpdatedSource: null,
                sections: {}
            };
        }

        const draftCount = toCount((raw as { draftCount?: unknown; draft_count?: unknown }).draftCount ?? (raw as { draft_count?: unknown }).draft_count);
        const approvedCount = toCount(
            (raw as { approvedCount?: unknown; approved_count?: unknown }).approvedCount ??
                (raw as { approved_count?: unknown }).approved_count
        );
        const qsaReviewedCount = toCount(
            (raw as { qsaReviewedCount?: unknown; qsa_reviewed_count?: unknown }).qsaReviewedCount ??
                (raw as { qsa_reviewed_count?: unknown }).qsa_reviewed_count
        );
        const lastUpdatedRaw =
            (raw as { lastUpdatedAt?: unknown; last_updated_at?: unknown }).lastUpdatedAt ??
            (raw as { last_updated_at?: unknown }).last_updated_at ??
            null;
        let lastUpdatedAt: string | null = null;
        if (typeof lastUpdatedRaw === "string") {
            lastUpdatedAt = lastUpdatedRaw;
        } else if (lastUpdatedRaw instanceof Date && !Number.isNaN(lastUpdatedRaw.getTime())) {
            lastUpdatedAt = lastUpdatedRaw.toISOString();
        }

        const lastUpdatedByRaw =
            (raw as { lastUpdatedBy?: unknown }).lastUpdatedBy ??
            (raw as { last_updated_by?: unknown }).last_updated_by ??
            (raw as { lastUpdatedByDisplayName?: unknown }).lastUpdatedByDisplayName ??
            (raw as { last_updated_by_display_name?: unknown }).last_updated_by_display_name ??
            null;

        const lastUpdatedBy = typeof lastUpdatedByRaw === "string" ? lastUpdatedByRaw.trim() || null : null;
        const lastUpdatedSourceRaw =
            (raw as { lastUpdatedSource?: unknown; last_updated_source?: unknown }).lastUpdatedSource ??
            (raw as { last_updated_source?: unknown }).last_updated_source;
        const lastUpdatedSource =
            lastUpdatedSourceRaw === "responses" || lastUpdatedSourceRaw === "activation"
                ? lastUpdatedSourceRaw
                : null;

        const sections = normalizeSections(
            (raw as { sections?: unknown; sectionSummaries?: unknown; section_summaries?: unknown }).sections ??
                (raw as { sectionSummaries?: unknown }).sectionSummaries ??
                (raw as { section_summaries?: unknown }).section_summaries
        );

        return {
            draftCount,
            approvedCount,
            qsaReviewedCount,
            lastUpdatedAt,
            lastUpdatedBy,
            lastUpdatedSource,
            sections
        };
    }

    clearCache(): void {
        this.cacheGeneration += 1;
        this.pendingAccessOverrides.clear();
        this.currentLoadSubscription?.unsubscribe();
        this.currentLoadSubscription = null;
        this.pendingRequest$ = null;
        this.pendingAccountLicenseCreditsRequest$ = null;
        this.taskAccessSnapshot = null;
        this.licensesSubject.next([]);
        this.accountLicenseCreditsSubject.next(null);
    }

    private publishTaskAccessChanges(licenses: readonly ProductLicenseSummary[]): void {
        const next = new Map(licenses.map((license): [number, string] => [license.id, JSON.stringify([
            license.accountId,
            license.merchantId,
            license.formType,
            license.formKey,
            license.featureSet,
            license.status,
            license.expirationDate,
            license.assignedIsaUserIdFromAssignments,
            license.assignedQsaUserIdFromAssignments,
            license.assignedQaUserIdFromAssignments,
            license.merchantRepUserId,
            [...license.editorIds].sort((a, b) => a - b),
            [...license.userReaderIds].sort((a, b) => a - b)
        ])]));
        const previous = this.taskAccessSnapshot;
        this.taskAccessSnapshot = next;
        if (!previous) return;
        for (const licenseId of new Set([...previous.keys(), ...next.keys()])) {
            if (previous.get(licenseId) !== next.get(licenseId)) {
                this.taskScopeChanges.assessmentChanged(licenseId, "license_refresh");
            }
        }
    }
}
